import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import { fetchSuperAdminDashboard } from '../dashboardApi';
import { useT } from '../i18n/translations';
import './Dashboard.css';

const DONUT_COLORS = ['#02c3a6', '#00334a', '#e53935'];
const RADIAL_COLORS = ['#02c3a6', '#02c3a6', '#fb8c00'];

const votingTrendBaseOptions = {
  options: {
    chart: { type: 'area', fontFamily: 'Cairo, sans-serif', toolbar: { show: false }, zoom: { enabled: false } },
    colors: ['#02c3a6', '#e53935', '#fb8c00'],
    fill: { type: 'solid', opacity: 0.35 },
    stroke: { curve: 'smooth', width: 2.5 },
    xaxis: { categories: [], labels: { style: { fontFamily: 'Cairo, sans-serif', fontSize: '11px' } } },
    yaxis: { labels: { style: { fontFamily: 'Cairo, sans-serif' } } },
    legend: { position: 'top', fontFamily: 'Cairo, sans-serif', fontSize: '12px', horizontalAlign: 'right' },
    tooltip: { style: { fontFamily: 'Cairo, sans-serif' } },
    grid: { borderColor: '#f0f4fa', strokeDashArray: 4 },
  },
};

const companiesBarBaseOptions = {
  options: {
    chart: { type: 'bar', fontFamily: 'Cairo, sans-serif', toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 7, columnWidth: '55%', distributed: true } },
    colors: ['#02c3a6', '#02c3a6', '#00334a', '#02c3a6', '#02c3a6', '#02c3a6', '#00334a'],
    xaxis: { categories: [], labels: { style: { fontFamily: 'Cairo, sans-serif', fontSize: '11px' } } },
    yaxis: { labels: { style: { fontFamily: 'Cairo, sans-serif' } } },
    legend: { show: false },
    tooltip: { style: { fontFamily: 'Cairo, sans-serif' } },
    grid: { borderColor: '#f0f4fa', strokeDashArray: 4 },
    dataLabels: { enabled: false },
  },
};

export default function SuperAdminDashboard() {
  const t = useT();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const payload = await fetchSuperAdminDashboard();
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kpis = data?.kpis ?? {
    companiesTotal: 0, companiesActive: 0, ceosTotal: 0,
    boardMembersTotal: 0, boardMembersActive: 0, shareholdersTotal: 0,
    decisionsTotal: 0, decisionsPending: 0, decisionsApproved: 0, decisionsRejected: 0,
  };

  const decisionsDonut = useMemo(() => ({
    options: {
      chart: { type: 'donut', fontFamily: 'Cairo, sans-serif' },
      labels: [t('sadmin.approved'), t('sadmin.pending'), t('sadmin.rejected')],
      colors: DONUT_COLORS,
      legend: { position: 'bottom', fontFamily: 'Cairo, sans-serif', fontSize: '13px' },
      dataLabels: { style: { fontFamily: 'Cairo, sans-serif' } },
      tooltip: { style: { fontFamily: 'Cairo, sans-serif' } },
    },
    series: data?.decisionsDistribution ?? [0, 0, 0],
  }), [data, t]);

  const votingTrendLine = useMemo(() => ({
    options: {
      ...votingTrendBaseOptions.options,
      xaxis: { ...votingTrendBaseOptions.options.xaxis, categories: data?.votesTrend?.categories ?? [] },
    },
    series: [
      { name: t('sadmin.approved'), data: data?.votesTrend?.approve ?? [] },
      { name: t('sadmin.rejected'), data: data?.votesTrend?.reject  ?? [] },
      { name: t('sadmin.abstain'),  data: data?.votesTrend?.abstain ?? [] },
    ],
  }), [data, t]);

  const companiesBar = useMemo(() => ({
    options: {
      ...companiesBarBaseOptions.options,
      xaxis: { ...companiesBarBaseOptions.options.xaxis, categories: data?.companiesBoard?.categories ?? [] },
    },
    series: [{ name: t('sadmin.members'), data: data?.companiesBoard?.values ?? [] }],
  }), [data, t]);

  const shareholdersRadial = useMemo(() => ({
    options: {
      chart: { type: 'radialBar', fontFamily: 'Cairo, sans-serif' },
      labels: data?.shareholderTypes?.labels ?? [],
      colors: RADIAL_COLORS,
      legend: { show: true, position: 'bottom', fontFamily: 'Cairo, sans-serif', fontSize: '12px' },
      tooltip: { style: { fontFamily: 'Cairo, sans-serif' } },
    },
    series: data?.shareholderTypes?.series ?? [],
  }), [data]);

  const statusMap = useMemo(() => ({
    approved: { label: t('sadmin.rowApproved'), cls: 'status-badge--approved' },
    pending:  { label: t('sadmin.rowPending'),  cls: 'status-badge--pending'  },
    rejected: { label: t('sadmin.rowRejected'), cls: 'status-badge--rejected' },
  }), [t]);

  const recentRows = data?.recentDecisions ?? [];

  return (
    <div className="dash">
      {loadError && <div style={{ marginBottom: 12, padding: '10px 14px', background: '#ffebee', color: '#c62828', borderRadius: 10 }}>{loadError}</div>}

      <div className="dash__welcome">
        <div className="dash__welcome-text">
          <h2>{t('sadmin.welcome')}</h2>
          <p>{t('sadmin.subtitle')}</p>
        </div>
      </div>

      <div className="dash__stats">
        <div className="stat-card stat-card--blue">
          <div className="stat-card__value">{kpis.companiesTotal}</div>
          <div className="stat-card__label">{t('sadmin.companies')}</div>
          <div className="stat-card__sub">{kpis.companiesActive} {t('sadmin.activeLabel')}</div>
        </div>
        <div className="stat-card stat-card--green">
          <div className="stat-card__value">{kpis.ceosTotal}</div>
          <div className="stat-card__label">{t('sadmin.ceos')}</div>
        </div>
        <div className="stat-card stat-card--orange">
          <div className="stat-card__value">{kpis.boardMembersTotal}</div>
          <div className="stat-card__label">{t('sadmin.boardMembers')}</div>
          <div className="stat-card__sub">{kpis.boardMembersActive} {t('sadmin.activeLabel')}</div>
        </div>
        <div className="stat-card stat-card--purple">
          <div className="stat-card__value">{kpis.shareholdersTotal}</div>
          <div className="stat-card__label">{t('sadmin.shareholders')}</div>
        </div>
      </div>

      <div className="dash__charts">
        <div className="chart-card">
          <div className="chart-card__header">
            <span className="chart-card__title">{t('sadmin.votingTrend')}</span>
            <span className="chart-card__badge">{loading ? '...' : t('sadmin.latest8Months')}</span>
          </div>
          <ReactApexChart options={votingTrendLine.options} series={votingTrendLine.series} type="area" height={240} />
        </div>
        <div className="chart-card">
          <div className="chart-card__header">
            <span className="chart-card__title">{t('sadmin.decisionsDistribution')}</span>
            <span className="chart-card__badge">{kpis.decisionsTotal}</span>
          </div>
          <ReactApexChart options={decisionsDonut.options} series={decisionsDonut.series} type="donut" height={240} />
        </div>
      </div>

      <div className="dash__charts dash__charts--bottom">
        <div className="chart-card">
          <div className="chart-card__header">
            <span className="chart-card__title">{t('sadmin.membersPerCompany')}</span>
            <span className="chart-card__badge">{data?.companiesBoard?.categories?.length ?? 0} {t('sadmin.companiesLabel')}</span>
          </div>
          <ReactApexChart options={companiesBar.options} series={companiesBar.series} type="bar" height={240} />
        </div>
        <div className="chart-card">
          <div className="chart-card__header">
            <span className="chart-card__title">{t('sadmin.shareholdersDistribution')}</span>
          </div>
          <ReactApexChart options={shareholdersRadial.options} series={shareholdersRadial.series} type="radialBar" height={240} />
        </div>
      </div>

      <div className="dash__recent">
        <div className="dash__recent-header">
          <span className="dash__recent-title">{t('sadmin.latestDecisions')}</span>
          <span className="dash__recent-link" onClick={() => navigate('/super-admin/decisions')} style={{ cursor: 'pointer' }}>
            {t('dashboard.viewAll')}
          </span>
        </div>
        <table className="dash__table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('sadmin.tableTitle')}</th>
              <th>{t('sadmin.tableCompany')}</th>
              <th>{t('sadmin.tableDate')}</th>
              <th>{t('sadmin.tableVotes')}</th>
              <th>{t('sadmin.tableStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {recentRows.map((row) => {
              const s = statusMap[row.status] ?? statusMap.pending;
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.title}</td>
                  <td>{row.company}</td>
                  <td>{row.date}</td>
                  <td>{row.votes}</td>
                  <td><span className={`status-badge ${s.cls}`}>{s.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
